/**
 * Where the cockpit view's **optical centre** goes.
 *
 * ## The original renders its world into a 320x161 window, not the whole frame
 *
 * **VERIFIED, all 33 `<PLANE>.DAT` files.** Every aircraft record carries two
 * rectangles in the block `docs/formats/missions-campaign.md` had listed only
 * as "viewport constants", each stored as four `int16` in `x0, x1, y0, y1`
 * order:
 *
 * | bytes | rect | value |
 * |---|---|---|
 * | 188-195 | the 3D window | `x 0..319, y 0..160` — **identical on all 33 files** |
 * | 180-187 | the unobstructed sub-window | `x 0..319, y t..b`, per aircraft |
 *
 * The second one is the giveaway that the first is what it looks like: its
 * `t`/`b` track each airframe's own painted obstruction — `0..139` for the
 * Camel's cowl, `16..157` for the Albatros family's centre-section cut-out,
 * `21..139` for the SPADs, `5..111` for the two-seaters and the Dr.I, and
 * `0..159` for the D.H.2, the one pusher in the fleet, whose pilot sits ahead
 * of everything. A pair of rectangles that share `x 0..319` and differ only in
 * how much of `y 0..160` each aeroplane leaves visible is a 3D window and the
 * hole its cockpit art leaves in it.
 *
 * So the world window is **320 x 161 logical pixels of the 320x200 frame, and
 * its centre is row 80** — not row 100. Two independent measurements agree:
 *
 * - `docs/game/ui-reference/cockpit-camel-forward.png` (a real DOS capture, 2x)
 *   has its horizon at logical row **81** — near-level flight over a flat
 *   world puts the horizon on the principal point, and row 100 is 19 rows away
 *   from where it actually is.
 * - the Camel's painted gunsight ring in that same capture spans logical rows
 *   **71 to ~89** against the sky, i.e. centred on row **80**. The original
 *   paints its sight on the 3D window's own centre, which is the only place a
 *   fixed forward gun's rounds can converge.
 *
 * ## What that means for this port, and what was wrong before
 *
 * `panel-layout.ts` presents the 320x200 composition at an **integer** scale
 * anchored bottom-centre (AD-009) over a 3D view that fills the whole window
 * with its principal point at the window's centre. Those two centres are not
 * the same point and the gap changes with every window size: at 1920x1040 the
 * panel lands at 1280x1000 offset (320, 40), so the painted sight sits at
 * device row `40 + 80 x 5 = 440` while the camera's axis is at row 520 — **80
 * device pixels apart** (the bullet-visibility lane measured ~75 off the art,
 * `re-sessions/bullets/shots/cockpit-3d-window-probe.png`).
 *
 * That gap is not cosmetic. A fixed forward gun fires *parallel to the
 * boresight*, so however the muzzles are placed, the far end of a burst
 * converges on the camera's principal point and nowhere else
 * (`gun-muzzles.ts`'s note: moving a muzzle 20 cm moves a round 100 m out by
 * about one pixel). With the principal point 80 px below the painted ring, a
 * burst aimed through the sight went *under* the cowl and was never seen from
 * the cockpit at all.
 *
 * The fix is a projection shear, not a moved panel: give the cockpit camera an
 * off-centre principal point that lands exactly on the painted sight, leaving
 * the angular scale of every pixel untouched. `three`'s `setViewOffset`
 * expresses precisely that — render an `W x H` sub-rectangle of a taller
 * virtual frame whose centre is where we want the axis — and `shearFor` below
 * is the arithmetic that picks the virtual frame. External views (chase,
 * fly-by, the kill camera's own lens) keep a centred principal point, because
 * they have no painted panel to agree with.
 *
 * ## The one thing this does *not* change
 *
 * The **field of view**. `CAMERA_FOV_DEG` spreads 55 degrees over the whole
 * window; the original spreads an unrecovered angle over 161 logical rows.
 * Matching that would letterbox the world into the painted window's own rect,
 * which is a different (and much larger) decision than putting the axis in the
 * right place — and the original's FOV constant has still not been recovered
 * (`docs/engineering/sun-blind-spot-design.md` §1.4 wants it too). `shearFor`
 * is therefore built to keep the rendered sub-window's vertical FOV *exactly*
 * `baseFovDeg`, so nothing that sizes a sprite against the camera changes.
 */

import type * as THREE from "three/webgpu";
import { logicalToCanvas, presentTransform } from "./panel-layout.ts";

/**
 * The original's own 3D window inside the 320x200 frame, `x0, x1, y0, y1`,
 * inclusive — `<PLANE>.DAT` bytes 188-195, byte-identical on all 33 aircraft
 * records. See this module's header for the evidence.
 */
export const WORLD_WINDOW = { left: 0, right: 319, top: 0, bottom: 160 } as const;

/** The logical pixel the world's optical axis passes through: `(159.5, 80)`. */
export const WORLD_WINDOW_CENTER = {
  x: (WORLD_WINDOW.left + WORLD_WINDOW.right) / 2,
  y: (WORLD_WINDOW.top + WORLD_WINDOW.bottom) / 2,
} as const;

/**
 * A pathological window (very wide and short, or very tall and narrow) can put
 * the painted sight far outside the viewport's own middle, and the virtual
 * frame grows as `H + 2|delta|`. Capping the shear at one and a half viewport
 * heights keeps the virtual vertical FOV under ~115 degrees so the projection
 * stays sane; at every ordinary window size the cap is nowhere near binding
 * (1920x1040 shears by 80 px, i.e. 0.077 H).
 */
const MAX_SHEAR_FRACTION = 1.5;

/** Device-pixel position of the painted gunsight — the point the world's axis must pass through. */
export function cockpitPrincipalPoint(widthPx: number, heightPx: number): { x: number; y: number } {
  const present = presentTransform(Math.max(widthPx, 1), Math.max(heightPx, 1));
  return logicalToCanvas(present, WORLD_WINDOW_CENTER.x, WORLD_WINDOW_CENTER.y);
}

/**
 * The arguments for `camera.setViewOffset` plus the `fov` that goes with them
 * — everything needed to move the principal point without changing what a
 * pixel is worth.
 */
export interface ProjectionShear {
  readonly fullWidth: number;
  readonly fullHeight: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly width: number;
  readonly height: number;
  /** `camera.fov` for the **virtual** frame; the rendered sub-window still subtends `baseFovDeg`. */
  readonly fovDeg: number;
}

/**
 * Derivation. `three` builds its frustum from the full virtual frame and then
 * takes the sub-rectangle out of it, so the axis (camera-space x = y = 0) sits
 * at virtual row `fullHeight / 2` and lands at sub-window row
 * `fullHeight / 2 - offsetY`. Requiring that to equal `py`, with the
 * sub-window inside the virtual frame in both directions, gives the smallest
 * virtual frame as `fullHeight = H + 2|py - H/2|` and
 * `offsetY = |delta| - delta`. The same two lines run for x.
 *
 * `setViewOffset` sets `aspect = fullWidth / fullHeight` itself, and `three`
 * scales the frustum's half-height by `tan(fov/2)`, so keeping the rendered
 * sub-window at `baseFovDeg` means growing `fov` by exactly the ratio the
 * virtual frame grew by: `tan(fov/2) = tan(baseFov/2) x fullHeight / H`.
 */
export function shearFor(
  widthPx: number,
  heightPx: number,
  principal: { readonly x: number; readonly y: number },
  baseFovDeg: number,
): ProjectionShear {
  const width = Math.max(widthPx, 1);
  const height = Math.max(heightPx, 1);
  const limitX = width * MAX_SHEAR_FRACTION;
  const limitY = height * MAX_SHEAR_FRACTION;
  const deltaX = Math.min(Math.max(principal.x - width / 2, -limitX), limitX);
  const deltaY = Math.min(Math.max(principal.y - height / 2, -limitY), limitY);
  const fullWidth = width + 2 * Math.abs(deltaX);
  const fullHeight = height + 2 * Math.abs(deltaY);
  const halfAngle = Math.atan(Math.tan((baseFovDeg * Math.PI) / 360) * (fullHeight / height));
  return {
    fullWidth,
    fullHeight,
    offsetX: Math.abs(deltaX) - deltaX,
    offsetY: Math.abs(deltaY) - deltaY,
    width,
    height,
    fovDeg: (halfAngle * 360) / Math.PI,
  };
}

/**
 * Points the camera's optical axis at the painted gunsight (cockpit) or at the
 * viewport's own centre (everything else). Idempotent and cheap enough to call
 * every frame: it early-outs unless the mode or the viewport actually changed,
 * so `updateProjectionMatrix` runs on transitions only.
 */
export function applyCockpitShear(
  camera: THREE.PerspectiveCamera,
  widthPx: number,
  heightPx: number,
  baseFovDeg: number,
  cockpit: boolean,
): void {
  const width = Math.max(widthPx, 1);
  const height = Math.max(heightPx, 1);
  if (!cockpit) {
    if (camera.view?.enabled !== true && camera.fov === baseFovDeg && camera.aspect === width / height) return;
    camera.clearViewOffset();
    camera.fov = baseFovDeg;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    return;
  }
  const shear = shearFor(width, height, cockpitPrincipalPoint(width, height), baseFovDeg);
  const view = camera.view;
  if (
    view?.enabled === true &&
    view.fullWidth === shear.fullWidth &&
    view.fullHeight === shear.fullHeight &&
    view.offsetX === shear.offsetX &&
    view.offsetY === shear.offsetY &&
    view.width === shear.width &&
    view.height === shear.height &&
    camera.fov === shear.fovDeg
  ) {
    return;
  }
  camera.fov = shear.fovDeg;
  camera.setViewOffset(shear.fullWidth, shear.fullHeight, shear.offsetX, shear.offsetY, shear.width, shear.height);
}

/**
 * The vertical field of view the **rendered** frame actually subtends.
 *
 * `camera.fov` is the virtual frame's angle once a shear is on, so anything
 * that converts screen pixels to metres (`three/sprite-screen-size.ts`, and
 * `combat-visuals.ts`'s bullet quads through it) has to ask for this instead
 * — otherwise every round and puff inflates by the shear ratio the moment the
 * player sits down in the cockpit.
 */
export function renderedFovDeg(camera: THREE.PerspectiveCamera): number {
  const view = camera.view;
  if (view?.enabled !== true || view.fullHeight <= 0) return camera.fov;
  const halfAngle = Math.atan(Math.tan((camera.fov * Math.PI) / 360) * (view.height / view.fullHeight));
  return (halfAngle * 360) / Math.PI;
}
